import { Fragment, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { homeCss, professionalengagementsCss } from '@digital_monorepo/styling';
import requestColumns from './requestColumns';

const columns = requestColumns.filter(column => column.key !== 'Comments');

const dateKey = value => {
  const match = String(value || '').match(/^(\d{4})-(\d{2})-(\d{2})(?:$|[T ])/);
  if (!match) return '';
  const [, year, month, day] = match;
  const date = new Date(Number(year), Number(month) - 1, Number(day));
  return date.getFullYear() === Number(year) && date.getMonth() + 1 === Number(month) && date.getDate() === Number(day)
    ? `${year}-${month}-${day}`
    : '';
};

const displayDate = value => {
  const key = dateKey(value);
  if (!key) return '—';
  const [year, month, day] = key.split('-');
  return `${day}/${month}/${year}`;
};

const isChecked = value => value === true || value === 1 || value === '1';

const StaffingRequestsSummary = ({ requests, loading, error, onRetry }) => {
  const [hideClosed, setHideClosed] = useState(true);
  const [filters, setFilters] = useState({});
  const [expandedGroups, setExpandedGroups] = useState(() => new Set());
  const now = new Date();
  const today = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;

  const { visible, groups } = useMemo(() => {
    const visible = requests.filter(request => {
      if (hideClosed && isChecked(request.Closed)) return false;
      return columns.every(column => {
        const filter = filters[column.key];
        if (!filter || filter === 'all') return true;
        if (column.type === 'boolean') return isChecked(request[column.key]) === (filter === 'yes');
        const value = column.type === 'date' ? dateKey(request[column.key]) : String(request[column.key] ?? '');
        return value.toLowerCase().includes(filter.toLowerCase());
      });
    });
    const byDate = new Map();
    visible.forEach(request => {
      const key = dateKey(request.StartDate);
      if (!byDate.has(key)) byDate.set(key, []);
      byDate.get(key).push(request);
    });
    const groups = [...byDate].sort(([a], [b]) => !a ? 1 : !b ? -1 : a.localeCompare(b));
    return { visible, groups };
  }, [requests, hideClosed, filters]);

  const toggleGroup = key => setExpandedGroups(previous => {
    const next = new Set(previous);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    return next;
  });

  const isOverdue = request => !isChecked(request.Closed) && !!dateKey(request.StartDate) && dateKey(request.StartDate) < today;
  const open = visible.filter(request => !isChecked(request.Closed));
  const overdue = open.filter(isOverdue).length;

  return (
    <section className={homeCss.requestsSection} aria-labelledby='summary-requests-title'>
      <div className={`${professionalengagementsCss.sectionHeader} ${homeCss.requestsHeader}`}>
        <h2 id='summary-requests-title' className={homeCss.chargeabilityTitle}>
          <span className={homeCss.sectionIndicator} aria-hidden='true' />
          Staffing Requests
        </h2>
        <div className={homeCss.requestsActions}>
          <label className={homeCss.hideClosedLabel}>
            <input type='checkbox' checked={hideClosed} onChange={event => setHideClosed(event.target.checked)} />
            Hide closed
          </label>
          <span className='workspace-count'>{visible.length} {visible.length === 1 ? 'request' : 'requests'}</span>
        </div>
      </div>
      {loading ? (
        <div className={homeCss.chargeabilityState} role='status'>Loading staffing requests…</div>
      ) : error ? (
        <div className={homeCss.chargeabilityState} role='alert'>
          Staffing requests could not be loaded.
          <button type='button' className='workspace-button' onClick={onRetry}>Retry</button>
        </div>
      ) : (
        <>
          <div className={homeCss.requestsOverview} aria-label='Open staffing request totals'>
            <div className={`${homeCss.requestsMetric} ${homeCss.requestsMetricTotal}`}>
              <span className={homeCss.requestsMetricLabel}>Total open</span>
              <strong>{open.length}</strong>
              <span className={homeCss.requestsMetricHint}>Matching current filters</span>
            </div>
            <div className={`${homeCss.requestsMetric} ${homeCss.requestsMetricOverdue}`}>
              <span className={homeCss.requestsMetricLabel}>Overdue</span>
              <strong>{overdue}</strong>
              <span className={homeCss.requestsMetricHint}>Start date has passed</span>
            </div>
            <div className={`${homeCss.requestsMetric} ${homeCss.requestsMetricUpcoming}`}>
              <span className={homeCss.requestsMetricLabel}>Not overdue</span>
              <strong>{open.length - overdue}</strong>
              <span className={homeCss.requestsMetricHint}>Today, later or no start date</span>
            </div>
          </div>
          <div className={professionalengagementsCss.tableWrapper}>
            <div className={`${professionalengagementsCss.tableContainer} ${professionalengagementsCss.overviewTableContainer}`}>
              <table className={`${professionalengagementsCss.table} ${homeCss.requestsTable}`}>
                <thead>
                  <tr className={professionalengagementsCss.headerRow}>
                    {columns.map(column => (
                      <th key={column.key} scope='col' className={professionalengagementsCss.th}>
                        <div className={homeCss.requestsColumnLabel}>{column.label}</div>
                        {column.type === 'boolean' ? (
                          <select
                            className={homeCss.requestsFilter}
                            aria-label={`Filter ${column.label}`}
                            value={filters[column.key] || 'all'}
                            onChange={event => setFilters(previous => ({ ...previous, [column.key]: event.target.value }))}>
                            <option value='all'>All</option>
                            <option value='yes'>Yes</option>
                            <option value='no'>No</option>
                          </select>
                        ) : (
                          <input
                            type='text'
                            className={homeCss.requestsFilter}
                            aria-label={`Filter ${column.label}`}
                            placeholder='Filter...'
                            value={filters[column.key] || ''}
                            onChange={event => setFilters(previous => ({ ...previous, [column.key]: event.target.value }))}
                          />
                        )}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {groups.length === 0 ? (
                    <tr><td colSpan={columns.length} className={homeCss.requestsEmpty}>
                      {requests.length === 0 ? 'No staffing requests yet.' : 'No requests match the current filters.'}
                    </td></tr>
                  ) : groups.map(([key, items]) => {
                    const expanded = expandedGroups.has(key);
                    const overdueCount = items.filter(isOverdue).length;
                    return (
                      <Fragment key={key || 'no-date'}>
                        <tr className={`${professionalengagementsCss.bodyRow} ${homeCss.requestsGroup} ${overdueCount ? homeCss.overdueGroup : ''}`} data-group={key || 'no-date'}>
                          <td colSpan={columns.length} className={professionalengagementsCss.td}>
                            <div className={homeCss.requestsGroupContent}>
                              <button type='button' className='workspace-button' aria-expanded={expanded} onClick={() => toggleGroup(key)}>
                                {expanded ? '− Less' : '+ More'}
                              </button>
                              <span className={homeCss.requestsGroupDate}>{key ? displayDate(key) : 'No start date'}</span>
                              <span className={homeCss.requestsGroupCount}>{items.length} {items.length === 1 ? 'request' : 'requests'}</span>
                              {overdueCount > 0 && <span className={homeCss.overdueCount}>{overdueCount} overdue</span>}
                            </div>
                          </td>
                        </tr>
                        {expanded && items.map(request => (
                          <tr key={request.RequestID} className={`${professionalengagementsCss.bodyRow} ${isOverdue(request) ? homeCss.overdueRow : ''}`}>
                            {columns.map(column => {
                              const value = column.type === 'boolean'
                                ? (isChecked(request[column.key]) ? 'Yes' : 'No')
                                : column.type === 'date' ? displayDate(request[column.key]) : String(request[column.key] ?? '') || '—';
                              return (
                                <td key={column.key} className={`${professionalengagementsCss.td} ${homeCss.requestsCell}`} title={value}>
                                  {value}
                                </td>
                              );
                            })}
                          </tr>
                        ))}
                      </Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </section>
  );
};

StaffingRequestsSummary.propTypes = {
  requests: PropTypes.arrayOf(PropTypes.object).isRequired,
  loading: PropTypes.bool.isRequired,
  error: PropTypes.bool.isRequired,
  onRetry: PropTypes.func.isRequired
};

export default StaffingRequestsSummary;
